import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStandalonePwa } from '../hooks/useStandalonePwa';
import { getPwaChromePortalRoot } from '../utils/modalPortal';
import { useRecentDashboards, formatRecentDate } from './RecentDashboards';
import { RecentDashboardActivityLinks } from './CrewTimesheetRecentButton';
import './PwaBottomNav.css';
import './RecentDashboards.css';

const NAV_ITEMS = [
  { id: 'projects', label: 'Projects', path: '/' },
  { id: 'contacts', label: 'Contacts', path: '/contacts/manage' },
  { id: 'accounts', label: 'Accounts', path: '/accounts' },
  { id: 'inventory', label: 'Inventory', path: '/inventory' },
];

function ProjectsIcon({ active }) {
  const stroke = active ? '#AD2226' : '#6B7280';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 7.5V19.5C3 20.3284 3.67157 21 4.5 21H9V14.25H15V21H19.5C20.3284 21 21 20.3284 21 19.5V7.5L12 3L3 7.5Z"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ContactsIcon({ active }) {
  const stroke = active ? '#AD2226' : '#6B7280';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M16 19V17C16 15.3431 14.6569 14 13 14H7C5.34315 14 4 15.3431 4 17V19"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="10" cy="8" r="3.25" stroke={stroke} strokeWidth="1.75" />
      <path
        d="M20 19V17C20 15.6118 19.1046 14.45 17.9 14.1"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M15 4.6C16.3807 4.6 17.5 5.71929 17.5 7.1C17.5 8.48071 16.3807 9.6 15 9.6"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AccountsIcon({ active }) {
  const stroke = active ? '#AD2226' : '#6B7280';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="8" width="16" height="12" rx="1.5" stroke={stroke} strokeWidth="1.75" />
      <path d="M9 8V6.5C9 5.11929 10.1193 4 11.5 4H12.5C13.8807 4 15 5.11929 15 6.5V8" stroke={stroke} strokeWidth="1.75" />
      <path d="M4 13H20" stroke={stroke} strokeWidth="1.75" />
    </svg>
  );
}

function InventoryIcon({ active }) {
  const stroke = active ? '#AD2226' : '#6B7280';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3L4 7V17L12 21L20 17V7L12 3Z"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M12 12L20 7" stroke={stroke} strokeWidth="1.75" />
      <path d="M12 12V21" stroke={stroke} strokeWidth="1.75" />
      <path d="M12 12L4 7" stroke={stroke} strokeWidth="1.75" />
    </svg>
  );
}

function MoreIcon({ active }) {
  const stroke = active ? '#AD2226' : '#6B7280';
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="6" cy="12" r="1.5" fill={stroke} />
      <circle cx="12" cy="12" r="1.5" fill={stroke} />
      <circle cx="18" cy="12" r="1.5" fill={stroke} />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="4" rx="1" stroke="#AD2226" strokeWidth="1.75" />
      <path
        d="M6 9V18C6 19.1046 6.89543 20 8 20H16C17.1046 20 18 19.1046 18 18V9"
        stroke="#AD2226"
        strokeWidth="1.75"
      />
      <path d="M10 13H14" stroke="#AD2226" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

const ICONS = {
  projects: ProjectsIcon,
  contacts: ContactsIcon,
  accounts: AccountsIcon,
  inventory: InventoryIcon,
  more: MoreIcon,
};

function getIsLandscape() {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.matchMedia('(orientation: landscape)').matches;
}

function useLandscapeOrientation() {
  const [isLandscape, setIsLandscape] = useState(getIsLandscape);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: landscape)');
    const sync = () => setIsLandscape(mediaQuery.matches);

    sync();
    mediaQuery.addEventListener('change', sync);
    window.addEventListener('orientationchange', sync);
    window.addEventListener('resize', sync);

    return () => {
      mediaQuery.removeEventListener('change', sync);
      window.removeEventListener('orientationchange', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  return isLandscape;
}

function getActiveTab(pathname, isMoreOpen) {
  if (isMoreOpen || pathname === '/archive') {
    return 'more';
  }
  if (pathname === '/' || pathname === '/dashboard' || pathname.startsWith('/project/')) {
    return 'projects';
  }
  if (pathname.startsWith('/contacts')) {
    return 'contacts';
  }
  if (pathname === '/accounts') {
    return 'accounts';
  }
  if (pathname === '/inventory') {
    return 'inventory';
  }
  if (pathname === '/equipment') {
    return 'more';
  }
  return null;
}

function EquipmentIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 16h12.5a3 3 0 0 0 2.9-2.2H21V10h-2.4l-1.5-2.6A2 2 0 0 0 15.3 6H9.5L7.8 8.5H3V16z"
        stroke="#AD2226"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="7.5" cy="17.5" r="1.6" stroke="#AD2226" strokeWidth="1.75" />
      <circle cx="16" cy="17.5" r="1.6" stroke="#AD2226" strokeWidth="1.75" />
    </svg>
  );
}

function PwaMoreSheet({ isOpen, onClose, recentDashboards, onRecentSelect, onReportSelect, onArchive, onEquipment }) {
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <button type="button" className="pwa-more-sheet__backdrop" onClick={onClose} aria-label="Close menu" />
      <div className="pwa-more-sheet" role="dialog" aria-label="Recent projects and archive">
        <button
          type="button"
          className="pwa-more-sheet__handle"
          onClick={onClose}
          aria-label="Close"
        />

        <section className="pwa-more-sheet__section">
          <h3 className="pwa-more-sheet__section-label">Recent</h3>
          {recentDashboards.length === 0 ? (
            <p className="pwa-more-sheet__empty">No recent projects. Open a project to see it here.</p>
          ) : (
            <ul className="pwa-more-sheet__list">
              {recentDashboards.map((dashboard, index) => (
                <li key={`${dashboard.projectId}-${dashboard.opportunityNo}-${index}`}>
                  <div className="pwa-more-sheet__recent-row">
                    <div className="pwa-more-sheet__recent-body">
                      <button
                        type="button"
                        className="pwa-more-sheet__recent-item"
                        onClick={() => onRecentSelect(dashboard)}
                      >
                        <span className="pwa-more-sheet__recent-icon" aria-hidden="true">
                          <ProjectsIcon active={false} />
                        </span>
                        <span className="pwa-more-sheet__recent-text">
                          <span className="pwa-more-sheet__recent-name">{dashboard.projectName}</span>
                          <span className="pwa-more-sheet__recent-meta">
                            {dashboard.projectId}
                            {dashboard.department ? ` · ${dashboard.department}` : ''}
                          </span>
                        </span>
                        <span className="pwa-more-sheet__recent-time">{formatRecentDate(dashboard.timestamp)}</span>
                      </button>
                      <div className="recent-dashboards-shortcuts">
                        <button
                          type="button"
                          className="recent-dashboards-report"
                          title="Open daily report"
                          onClick={() => onReportSelect(dashboard)}
                        >
                          Report
                        </button>
                        <RecentDashboardActivityLinks
                          dashboard={dashboard}
                          onFlowStarted={onClose}
                        />
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <button type="button" className="pwa-more-sheet__archive" onClick={onEquipment}>
          <EquipmentIcon />
          <span>Equipment</span>
        </button>
        <button type="button" className="pwa-more-sheet__archive" onClick={onArchive}>
          <ArchiveIcon />
          <span>Archive</span>
        </button>
      </div>
    </>
  );
}

export default function PwaBottomNav() {
  const isStandalone = useStandalonePwa();
  const isLandscape = useLandscapeOrientation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const { recentDashboards, handleDashboardClick, handleReportClick } = useRecentDashboards(() => setIsMoreOpen(false));
  const shouldShowBottomNav = isStandalone && !isLandscape;

  const activeTab = useMemo(
    () => getActiveTab(location.pathname, isMoreOpen),
    [location.pathname, isMoreOpen]
  );

  useEffect(() => {
    setIsMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isLandscape) {
      setIsMoreOpen(false);
    }
  }, [isLandscape]);

  useEffect(() => {
    if (!shouldShowBottomNav) {
      document.documentElement.classList.remove('nettles-pwa-has-bottom-nav');
      document.documentElement.classList.remove('nettles-pwa-more-open');
      return undefined;
    }

    document.documentElement.classList.add('nettles-pwa-has-bottom-nav');
    document.documentElement.classList.toggle('nettles-pwa-more-open', isMoreOpen);

    return () => {
      document.documentElement.classList.remove('nettles-pwa-has-bottom-nav');
      document.documentElement.classList.remove('nettles-pwa-more-open');
    };
  }, [shouldShowBottomNav, isMoreOpen]);

  if (!shouldShowBottomNav) {
    return null;
  }

  const handleNav = (item) => {
    setIsMoreOpen(false);
    if (location.pathname !== item.path) {
      navigate(item.path);
    }
    window.scrollTo(0, 0);
  };

  const handleMore = () => {
    setIsMoreOpen((open) => !open);
  };

  const handleArchive = () => {
    setIsMoreOpen(false);
    navigate('/archive');
    window.scrollTo(0, 0);
  };

  const handleEquipment = () => {
    setIsMoreOpen(false);
    navigate('/equipment');
    window.scrollTo(0, 0);
  };

  const portalRoot = getPwaChromePortalRoot();
  if (!portalRoot) {
    return null;
  }

  return createPortal(
    <>
      <nav className="pwa-bottom-nav" aria-label="Main">
        {NAV_ITEMS.map((item) => {
          const Icon = ICONS[item.id];
          const isActive = activeTab === item.id;

          return (
            <button
              key={item.id}
              type="button"
              className={`pwa-bottom-nav__item${isActive ? ' pwa-bottom-nav__item--active' : ''}`}
              onClick={() => handleNav(item)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="pwa-bottom-nav__icon" aria-hidden="true">
                <Icon active={isActive} />
              </span>
              <span className="pwa-bottom-nav__label">{item.label}</span>
              <span className="pwa-bottom-nav__indicator" aria-hidden="true" />
            </button>
          );
        })}

        <button
          type="button"
          className={`pwa-bottom-nav__item${activeTab === 'more' ? ' pwa-bottom-nav__item--active' : ''}`}
          onClick={handleMore}
          aria-expanded={isMoreOpen}
          aria-haspopup="dialog"
        >
          <span className="pwa-bottom-nav__icon" aria-hidden="true">
            <MoreIcon active={activeTab === 'more'} />
          </span>
          <span className="pwa-bottom-nav__label">More</span>
          <span className="pwa-bottom-nav__indicator" aria-hidden="true" />
        </button>
      </nav>

      <PwaMoreSheet
        isOpen={isMoreOpen}
        onClose={() => setIsMoreOpen(false)}
        recentDashboards={recentDashboards}
        onRecentSelect={handleDashboardClick}
        onReportSelect={handleReportClick}
        onArchive={handleArchive}
        onEquipment={handleEquipment}
      />
    </>,
    portalRoot
  );
}
